import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
  Form
} from 'react-bootstrap';
import { FiAlertTriangle, FiSend, FiRefreshCw, FiFilter } from 'react-icons/fi';
import { TowingEligibleClient, TowingEligiblePlate, MonitoringList } from '../Types';
import { getTowingEligibleClients } from '../requests/towingNotification.request';
import { getMonitoringList } from '../requests/monitoring.request';
import TowingCommunicationModal from '../Components/TowingCommunication/TowingCommunicationModal';

const TowingPage: React.FC = () => {
  const [clients, setClients] = useState<TowingEligibleClient[]>([]);
  const [monitoringLists, setMonitoringLists] = useState<MonitoringList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedMonitoringListId, setSelectedMonitoringListId] = useState<string>('');

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTowingEligibleClients();
      if (response?.success && response.data) {
        setClients(response.data);
      } else {
        setError(response?.errors?.[0] || 'Failed to fetch clients');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMonitoringLists = useCallback(async () => {
    try {
      const response = await getMonitoringList();
      if (response) {
        setMonitoringLists(response);
      }
    } catch (err: any) {
      console.error('Failed to fetch monitoring lists:', err);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchMonitoringLists();
  }, [fetchClients, fetchMonitoringLists]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const filteredClients = useMemo(() => {
    if (!selectedMonitoringListId) return clients;
    return clients.filter((c) => c.monitoringListId === selectedMonitoringListId);
  }, [clients, selectedMonitoringListId]);

  const totalPlatesAtRisk = filteredClients.reduce(
    (sum, c) => sum + (c.plates?.filter((p) => p.isEligibleForTowing).length || 0),
    0
  );

  const totalInterestAtRisk = filteredClients.reduce((sum, c) => sum + c.totalInterestOwed, 0);

  const renderPlateBreakdown = (plates: TowingEligiblePlate[]) => {
    if (!plates || plates.length === 0) {
      return <span className="text-muted">No plates</span>;
    }

    return (
      <div className="d-flex flex-column gap-1">
        {plates.map((plate) => (
          <div
            key={plate.plateId}
            className={`d-flex align-items-center gap-2 p-1 rounded ${
              plate.isEligibleForTowing ? 'bg-danger bg-opacity-10' : ''
            }`}>
            <Badge bg={plate.isEligibleForTowing ? 'danger' : 'secondary'} className="fw-normal">
              {plate.plateNumber}
            </Badge>
            <span className={plate.isEligibleForTowing ? 'text-danger fw-semibold' : 'text-muted'}>
              ${plate.interestOwed.toFixed(2)}
            </span>
            {plate.isEligibleForTowing && (
              <FiAlertTriangle className="text-danger" title="Eligible for towing" />
            )}
            <small className="text-muted">({plate.unpaidTicketCount} tickets)</small>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1 d-flex align-items-center gap-2">
                <FiAlertTriangle className="text-danger" />
                Towing
              </h2>
              <p className="text-muted mb-0">
                Towing eligibility is calculated <strong>per plate</strong>. A plate is at risk when
                its interest exceeds $350.
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={fetchClients}
                disabled={loading}
                className="d-flex align-items-center gap-2">
                <FiRefreshCw className={loading ? 'spin' : ''} />
                Refresh
              </Button>
              <Button
                variant="danger"
                onClick={() => setShowModal(true)}
                disabled={filteredClients.length === 0}
                className="d-flex align-items-center gap-2">
                <FiSend />
                Send Notifications
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Row className="mb-4">
        <Col md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center gap-2 mb-2">
                <FiFilter className="text-muted" />
                <span className="fw-semibold">Filter by Monitoring List</span>
              </div>
              <Form.Select
                value={selectedMonitoringListId}
                onChange={(e) => setSelectedMonitoringListId(e.target.value)}>
                <option value="">All Monitoring Lists</option>
                {monitoringLists
                  .filter((ml) => ml.name.toLowerCase() !== 'unassigned')
                  .map((ml) => (
                    <option key={ml.id} value={ml.id}>
                      {ml.name}
                    </option>
                  ))}
              </Form.Select>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={3}>
          <Card className="bg-danger text-white">
            <Card.Body className="text-center">
              <h3 className="mb-1">{filteredClients.length}</h3>
              <small>Clients at Risk</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="bg-warning text-dark">
            <Card.Body className="text-center">
              <h3 className="mb-1">{totalPlatesAtRisk}</h3>
              <small>Plates at Risk</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="bg-info text-white">
            <Card.Body className="text-center">
              <h3 className="mb-1">${totalInterestAtRisk.toFixed(2)}</h3>
              <small>Total Interest Owed</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="bg-secondary text-white">
            <Card.Body className="text-center">
              <h3 className="mb-1">$350</h3>
              <small>Threshold per Plate</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header className="bg-danger text-white">
          <h5 className="mb-0 d-flex align-items-center gap-2">
            <FiAlertTriangle />
            Clients with Plates Eligible for Towing
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="danger" />
              <p className="mt-3 text-muted">Loading clients...</p>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-success mb-0">
                {selectedMonitoringListId
                  ? 'No clients in this monitoring list have plates at risk of towing.'
                  : 'No clients currently have plates at risk of towing.'}
              </p>
            </div>
          ) : (
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Client Name</th>
                  <th>Monitoring List</th>
                  <th>Contact</th>
                  <th>Plates & Interest</th>
                  <th>Last Notified</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <tr key={client.clientId}>
                    <td className="fw-semibold">{client.clientName}</td>
                    <td>
                      <Badge bg="info" className="fw-normal">
                        {client.monitoringListName}
                      </Badge>
                    </td>
                    <td>
                      <div className="small">
                        <div className={!client.emailAddress ? 'text-danger' : ''}>
                          {client.emailAddress || 'No email'}
                        </div>
                        <div className={!client.phoneNumber ? 'text-danger' : 'text-muted'}>
                          {client.phoneNumber || 'No phone'}
                        </div>
                      </div>
                    </td>
                    <td>{renderPlateBreakdown(client.plates)}</td>
                    <td>
                      <span className="text-muted small">
                        {formatDate(client.lastTowingNotificationSentAt)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <TowingCommunicationModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onNotificationsSent={fetchClients}
        clients={filteredClients}
      />

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Container>
  );
};

export default TowingPage;
